import React, { useState, useEffect } from "react";
import "../styles/ManageMember.css";
import SideBar from "../components/SideBar";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useLocation, useNavigate } from "react-router-dom";
import { retailerService } from "../services/RetailerServices";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
});

function ManageRetailerShop() {
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("all");
  const [region, setRegion] = useState("all");
  const [status, setStatus] = useState("all");
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [retails, setRetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);
  const [allRegionsData, setAllRegionsData] = useState([]);
  const [totalRetailers, setTotalRetailers] = useState(0);
  const [filteredRetailersCount, setFilteredRetailersCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // Remove unnecessary states and effects
  useEffect(() => {
    if (location.state && location.state.message) {
      setSnackMessage(location.state.message);
      setSnackOpen(true);
    }
  }, [location.state]);

  // Fetch retailers when filters change
  useEffect(() => {
    fetchRetails();
  }, [currentPage, search, status]);

  // Update available regions when country filter changes
  useEffect(() => {
    if (country === "all") {
      const uniqueRegions = [...new Set(allRegionsData.map(item => item.region))].sort();
      setAvailableRegions(uniqueRegions);
    } else {
      const countryRegions = allRegionsData
        .filter(item => item.country && item.country.toLowerCase() === country.toLowerCase())
        .map(item => item.region);
      const uniqueRegions = [...new Set(countryRegions)].sort();
      setAvailableRegions(uniqueRegions);
    }
    
    setRegion("all");
  }, [country, allRegionsData]);

  const fetchRetails = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: 10,
        ...(status !== 'all' && { status }),
        ...(search && { search })
      };

      const response = await retailerService.getAllRetailers(params);
      
      if (response.users && Array.isArray(response.users)) {
        setRetails(response.users);
        
        // Extract unique countries from the response
        const countries = [...new Set(response.users
          .map(retail => retail.country)
          .filter(country => country && country.trim() !== '')
        )].sort();
        
        setAvailableCountries(countries);

        // Store all country-region pairs for filtering
        const regionsData = response.users
          .map(retail => ({
            country: retail.country,
            region: retail.region
          }))
          .filter(item => item.region && item.region.trim() !== '' && item.country && item.country.trim() !== '');
        
        setAllRegionsData(regionsData);

        // Set initial available regions
        const uniqueRegions = [...new Set(regionsData.map(item => item.region))].sort();
        setAvailableRegions(uniqueRegions);
        
        // Set total retailers from pagination
        if (response.pagination && response.pagination.total) {
          setTotalRetailers(response.pagination.total);
          // Calculate filtered count based on search/status filters
          if (search || status !== 'all') {
            // If there are active filters, filtered count is the same as total since API already filtered
            setFilteredRetailersCount(response.pagination.total);
          } else {
            // No filters, so filtered count = total count
            setFilteredRetailersCount(response.pagination.total);
          }
        }
      } else {
        setRetails([]);
        setAvailableCountries([]);
        setAvailableRegions([]);
        setAllRegionsData([]);
        setTotalRetailers(0);
        setFilteredRetailersCount(0);
      }
    } catch (error) {
      console.error('Error fetching retail businesses:', error);
      setSnackMessage('Failed to load retail businesses');
      setSnackOpen(true);
      setRetails([]);
      setAvailableCountries([]);
      setAvailableRegions([]);
      setAllRegionsData([]);
      setTotalRetailers(0);
      setFilteredRetailersCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackClose = () => setSnackOpen(false);

  const goToCreateAccount = () => {
    navigate("/create-retail-account");
  };

  // Format API data to match your structure
  const formatRetails = (apiRetails) => {
    return apiRetails.map(retail => ({
      id: retail.retail_business_id,
      fullName: `${retail.first_name} ${retail.last_name}`,
      firstName: retail.first_name,
      surname: retail.last_name,
      email: retail.email,
      mobile: retail.phone_number,
      industry: retail.shop_details?.retail_type || 'Not specified',
      retailSubType: retail.shop_details?.retail_sub_type || 'Not specified',
      retailName: retail.shop_details?.retail_name || 'Not specified',
      address: {
        line1: retail.address_line1 || '',
        line2: retail.address_line2 || '',
        line3: retail.address_line3 || '',
        city: retail.city || '',
        county: retail.region || 'N/A',
        country: retail.country || 'N/A',
        postcode: retail.postcode || ''
      },
      status: retail.current_status ? retail.current_status.charAt(0).toUpperCase() + retail.current_status.slice(1) : 'Awaiting',
      notes: retail.status_note || '',
      referrer_name: retail.referer_name || 'N/A',
      referrer_id: retail.referer_id || 'N/A',
      referrer_agent_type: retail.referer_role || 'N/A',
      referrer_country: retail.referer_country || 'N/A',
      referrer_region: retail.referer_region || 'N/A',
      referrer_email: retail.referer_email || 'N/A',
      referrer_phone: retail.referer_phone_number || 'N/A',
      value: "0 TopCoin",
      photo_url: retail.photo_url,
      shop_details: retail.shop_details,
      created_at: retail.created_at,
      ownerCountry: retail.country || 'N/A',
      ...retail
    }));
  };

  const handleRowClick = (retail) => {
    navigate("/retailer-shop-details", { state: { retail } });
  };

  // Use formatted retails from API - apply additional country and region filters
  const formattedRetails = retails.length > 0 ? formatRetails(retails) : [];
  
  // Apply country and region filters on the client side since API doesn't support them
  const filteredRetails = formattedRetails.filter((retail) => {
    const countryMatch = country === "all" || 
      (retail.ownerCountry && retail.ownerCountry.toLowerCase() === country.toLowerCase());
    
    const regionMatch = region === "all" || 
      (retail.address.county && retail.address.county.toLowerCase() === region.toLowerCase());

    return countryMatch && regionMatch;
  });

  // Calculate pagination based on the current page's filtered data
  // Note: Since API returns paginated data, we can only paginate within the current page's results
  const itemsPerPage = 10;
  const totalPages = Math.ceil(totalRetailers / itemsPerPage);

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{border:0, boxShadow:"none"}}>
          <div className="header-top">
            <h2 className="header-title">Manage Retail Business</h2>
            <button className="action-btn" onClick={goToCreateAccount}>+ Add New Retailer Shop</button>
          </div>

          {/* Retail Business Statistics Section - Added */}
          <div className="user-statistics">
            <div className="stat-item">
              <span className="stat-label">Total Retail Businesses:</span>
              <span className="stat-value">{totalRetailers}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Filtered Retailers:</span>
              <span className="stat-value">{filteredRetailersCount}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Current Page:</span>
              <span className="stat-value">
                {filteredRetails.length}
              </span>
            </div>
          </div>

          <div className="filters">
            <div className="input-group">
              <label>Filter by country:</label>
              <select value={country} onChange={(e) => setCountry(e.target.value)} className="filter-select">
                <option value="all">All Countries</option>
                {availableCountries.map((countryName, index) => (
                  <option key={index} value={countryName.toLowerCase()}>
                    {countryName}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label>Filter by region:</label>
              <select 
                value={region} 
                onChange={(e) => setRegion(e.target.value)} 
                className="filter-select"
                disabled={availableRegions.length === 0}
              >
                <option value="all">
                  {availableRegions.length === 0 ? "No regions available" : "All Regions"}
                </option>
                {availableRegions.map((regionName, index) => (
                  <option key={index} value={regionName.toLowerCase()}>
                    {regionName}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label>Filter by status:</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="filter-select">
                <option value="all">All</option>
                <option value="approved">Approved</option>
                <option value="awaiting">Awaiting</option>
                <option value="suspended">Suspended</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="input-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by ID, name, email, shop name or industry"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="member-table">
            <thead>
              <tr>
                <th style={{ width: "80px" }}>ID</th>
                <th style={{ width: "150px" }}>Owner Name</th>
                <th style={{ width: "150px" }}>Retail/Shop Name</th>
                <th style={{ width: "130px" }}>Telephone</th>
                <th style={{ width: "200px" }}>Email</th>
                <th style={{ width: "130px" }}>Type of Industry</th>
                <th style={{ width: "120px" }}>Country</th>
                <th style={{ width: "100px" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center" }}>
                    <div className="loading-message">Loading Retail Businesses...</div>
                  </td>
                </tr>
              ) : filteredRetails.length > 0 ? (
                filteredRetails.map((retail, i) => (
                  <tr 
                    key={retail.id || i}
                    onClick={() => handleRowClick(retail)}
                  >
                    <td>{retail.id}</td>
                    <td>{retail.fullName}</td>
                    <td>{retail.retailName}</td>
                    <td>{retail.mobile}</td>
                    <td>{retail.email}</td>
                    <td>{retail.industry}</td>
                    <td>{retail.ownerCountry}</td>
                    <td>
                      <span className={`status ${retail.status.toLowerCase()}`}>{retail.status}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center" }}>
                    {filteredRetailersCount === 0 && totalRetailers > 0 
                      ? "No retail businesses match your filters" 
                      : "No retail businesses found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {!loading && totalRetailers > 0 && (
            <div className="pagination">
              <button 
                className="page-btn" 
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                &lt;
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className="page-btn" 
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </div>

      <Snackbar 
        open={snackOpen} 
        autoHideDuration={3000} 
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert 
          severity="success"
          icon={<span style={{ fontWeight: "bold", fontSize: "18px", color:'rgba(51, 178, 51, 1' }}>✔</span>}
          sx={{
              backgroundColor: "transparent",
              backdropFilter: "blur(8px)",
              color: "#000",
              border: "2px solid rgba(51, 178, 51, 1)",
              borderRadius: "10px",
              boxShadow: "none",
              fontSize: "15px",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              height: '25px',
              width: '200px',
            }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ManageRetailerShop;